<template>
  <div class="notification-bell">
    <div class="relative">
      <a href="#" 
        @click.prevent="toggleDropdown" 
        class="notification"
        :class="{ 'notification--active': unreadCount > 0 }"
      >
        <i data-feather="bell" class="notification__icon"></i>
        <div v-if="unreadCount > 0" class="notification__badge">
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </div>
      </a>

      <!-- Dropdown -->
      <transition name="fade">
        <div v-if="showDropdown" class="notification-dropdown">
          <div class="notification-header">
            <h3>Notifications</h3>
            <button 
              v-if="unreadCount > 0" 
              @click="markAllAsRead" 
              class="mark-all-read-btn"
            >
              Mark all as read
            </button>
          </div>

          <div class="notification-list">
            <!-- Loading state -->
            <div v-if="loading" class="notification-loading">
              <i data-feather="loader" class="feather-spin"></i> Loading...
            </div>

            <!-- Empty state -->
            <div v-else-if="notifications.length === 0" class="notification-empty">
              <i data-feather="inbox"></i>
              <p>No notifications yet</p>
            </div>

            <!-- Notifications -->
            <div 
              v-else
              v-for="notification in notifications" 
              :key="notification.id"
              class="notification-item"
              :class="{ 'unread': notification.status === 'unread' }"
              @click="handleNotificationClick(notification)"
            >
              <div class="notification-icon" :class="`type-${notification.notification_type}`">
                <i :data-feather="getNotificationIcon(notification.notification_type)"></i>
              </div>
              
              <div class="notification-content">
                <div class="notification-title">{{ notification.title }}</div>
                <div class="notification-message">{{ notification.message }}</div>
                <div class="notification-time">{{ formatTime(notification.created_at) }}</div>
              </div>

              <button 
                @click.stop="deleteNotification(notification.id)" 
                class="notification-delete"
              >
                <i data-feather="x"></i>
              </button>
            </div>
          </div>

          <div class="notification-footer">
            <button @click="viewAllNotifications" class="view-all-btn">
              View All Notifications
            </button>
            <div class="system-notification-status">
              <button 
                v-if="systemNotificationPermission !== 'granted'" 
                @click="enableSystemNotifications"
                class="enable-system-notifications-btn"
              >
                <i data-feather="bell"></i>
                Enable System Notifications
              </button>
              <transition name="fade">
                <div v-if="showEnabledMessage" class="system-notification-enabled">
                  <i data-feather="check-circle"></i>
                  System notifications enabled
                </div>
              </transition>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'NotificationBell',
  data() {
    return {
      showDropdown: false,
      systemNotificationPermission: 'default',
      showEnabledMessage: false
    }
  },
  computed: {
    ...mapGetters('Notifications', [
      'allNotifications',
      'unreadCount',
      'loading',
      'isConnected'
    ]),
    notifications() {
      // Show only the latest 10 notifications in dropdown
      return this.allNotifications.slice(0, 10)
    }
  },
  methods: {
    ...mapActions('Notifications', [
      'markAsRead',
      'markAllAsRead',
      'deleteNotification',
      'fetchNotifications'
    ]),
    toggleDropdown() {
      this.showDropdown = !this.showDropdown
    },
    async handleNotificationClick(notification) {
      if (notification.status === 'unread') {
        await this.markAsRead(notification.id)
      }
      
      // Handle notification click based on metadata
      if (notification.metadata && notification.metadata.route) {
        this.$router.push(notification.metadata.route)
        this.showDropdown = false
      }
    },
    getNotificationIcon(type) {
      const icons = {
        info: 'info',
        success: 'check-circle',
        warning: 'alert-triangle',
        error: 'alert-circle'
      }
      return icons[type] || icons.info
    },
    formatTime(timestamp) {
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`
      
      return date.toLocaleDateString()
    },
    viewAllNotifications() {
      this.$router.push('/notifications')
      this.showDropdown = false
    },
    async enableSystemNotifications() {
      await this.$store.dispatch('Notifications/requestNotificationPermission')
      this.updateNotificationPermission()
      
      // Show success message temporarily
      if (this.systemNotificationPermission === 'granted') {
        this.showEnabledMessage = true
        setTimeout(() => {
          this.showEnabledMessage = false
        }, 3000)
      }
    },
    updateNotificationPermission() {
      if ('Notification' in window) {
        this.systemNotificationPermission = Notification.permission
      }
    }
  },
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.showDropdown = false
      }
    })
    
    // Check notification permission status
    this.updateNotificationPermission()
    
    // Initialize Feather icons
    this.$nextTick(() => {
      if (window.feather) {
        window.feather.replace()
      }
    })
  },
  updated() {
    // Re-initialize Feather icons after updates
    this.$nextTick(() => {
      if (window.feather) {
        window.feather.replace()
      }
    })
  }
}
</script>

<style scoped>
.notification-bell {
  position: relative;
  display: inline-block;
}

.notification {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification__badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 9999px;
  min-width: 18px;
  text-align: center;
  line-height: 1.2;
}

.notification-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  width: 380px;
  max-height: 500px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.notification-header h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #1e293b;
}

.mark-all-read-btn {
  background: transparent;
  border: none;
  color: #3b82f6;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;
}

.mark-all-read-btn:hover {
  text-decoration: underline;
}

.notification-list {
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
}

.notification-loading,
.notification-empty {
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
}

.notification-loading i,
.notification-empty i {
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  display: block;
}

.feather-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.notification-item {
  display: flex;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background-color 0.2s;
}

.notification-item:hover {
  background-color: #f8fafc;
}

.notification-item.unread {
  background-color: #eff6ff;
}

.notification-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 16px;
}

.notification-icon.type-info {
  background: #dbeafe;
  color: #3b82f6;
}

.notification-icon.type-success {
  background: #dcfce7;
  color: #22c55e;
}

.notification-icon.type-warning {
  background: #fef3c7;
  color: #f59e0b;
}

.notification-icon.type-error {
  background: #fee2e2;
  color: #ef4444;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.notification-message {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-time {
  font-size: 12px;
  color: #94a3b8;
}

.notification-delete {
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: #cbd5e1;
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
  transition: color 0.2s;
}

.notification-delete:hover {
  color: #ef4444;
}

.notification-footer {
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
}

.view-all-btn {
  width: 100%;
  background: transparent;
  border: none;
  color: #3b82f6;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
}

.view-all-btn:hover {
  background-color: #f8fafc;
}

.system-notification-status {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
}

.enable-system-notifications-btn {
  width: 100%;
  background: #3b82f6;
  color: white;
  border: none;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.enable-system-notifications-btn:hover {
  background: #2563eb;
}

.enable-system-notifications-btn i {
  width: 14px;
  height: 14px;
}

.system-notification-enabled {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  color: #22c55e;
  padding: 6px;
}

.system-notification-enabled i {
  width: 14px;
  height: 14px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
